<section class="dashboard">

  <div class="mode-toggle">
    <button
      #tabBtn
      class="mode-toggle__tab"
      [class.mode-toggle__tab--active]="activeTab === 'webhook'"
      (click)="switchTab('webhook')">
      Webhook
    </button>
    <button
      #tabBtn
      class="mode-toggle__tab"
      [class.mode-toggle__tab--active]="activeTab === 'credentials'"
      (click)="switchTab('credentials')">
      Carrier Credentials
    </button>
    <button
      #tabBtn
      *ngIf="pollingMetricsEnabled"
      class="mode-toggle__tab"
      [class.mode-toggle__tab--active]="activeTab === 'polling'"
      (click)="switchTab('polling')">
      Polling
    </button>
    <span
      class="mode-toggle__indicator"
      [style.left.px]="indicatorLeft"
      [style.width.px]="indicatorWidth">
    </span>
  </div>

  <!-- Webhook Tab -->
  <div class="mode-content" *ngIf="activeTab === 'webhook' && !loadingConfig">
    <p class="mode-hint">Configure webhook notifications for your tracking subscriptions</p>

    <div class="card">
      <div class="card-body">

        <div class="d-flex align-items-center mb-3">
          <label class="field-label mb-0 me-3">Status</label>
          <span class="status-badge" [class.status-badge--active]="activated">
            {{ activated ? 'Active' : 'Inactive' }}
          </span>
        </div>

        <div class="mb-3">
          <label class="field-label">Target URL</label>
          <div class="input-group">
            <input
              type="url"
              class="form-control"
              [(ngModel)]="webhookUrl"
              (keyup.enter)="saveWebhook()"
              placeholder="https://your-endpoint.com/webhook">
            <button
              class="btn btn-primary px-4"
              [disabled]="!webhookUrl.trim() || saving"
              (click)="saveWebhook()">
              <span *ngIf="saving" class="spinner-border spinner-border-sm me-1"></span>
              Save
            </button>
          </div>
        </div>

        <!-- Only show secret input when webhook is not activated -->
        <div class="mb-3" *ngIf="!activated">
          <hr>
          <label class="field-label">Activate</label>
          <p class="mode-hint" style="margin-bottom: 0.75rem;">Enter the secret that was sent to your target URL</p>
          <div class="input-group">
            <input
              type="text"
              class="form-control"
              [(ngModel)]="webhookSecret"
              (keyup.enter)="activateWebhook()"
              placeholder="Enter secret">
            <button
              class="btn btn-primary px-4"
              [disabled]="!webhookSecret.trim() || activating"
              (click)="activateWebhook()">
              <span *ngIf="activating" class="spinner-border spinner-border-sm me-1"></span>
              Activate
            </button>
          </div>
        </div>

        <div class="alert" [class.alert-success]="messageType === 'success'" [class.alert-danger]="messageType === 'danger'" *ngIf="message">
          {{ message }}
        </div>
      </div>
    </div>
  </div>

  <!-- Carrier Credentials Tab -->
  <div class="mode-content" *ngIf="activeTab === 'credentials' && !loadingConfig">
    <p class="mode-hint">Configure your own API credentials for each carrier. These override the default shared credentials.</p>

    <div class="carrier-grid">
      <div class="card carrier-card" [class.carrier-card--disabled]="!supportedCarriers.has(carrier.scac)" *ngFor="let carrier of carriers">
        <div class="card-body">
          <div class="carrier-header">
            <img [src]="'assets/carriers_logo/' + carrier.scac + '.png'" [alt]="carrier.name" class="carrier-logo" [class.carrier-logo--wide]="carrier.scac === 'CMAU'" [class.carrier-logo--compact]="carrier.scac === 'HLCU'">
            <span class="carrier-scac">{{ carrier.scac }}</span>
            <ng-container *ngIf="supportedCarriers.has(carrier.scac)">
              <span class="status-badge status-badge--sm" [class.status-badge--active]="hasCredentials(carrier.scac)">
                {{ hasCredentials(carrier.scac) ? 'Configured' : 'Not set' }}
              </span>
            </ng-container>
            <ng-container *ngIf="!supportedCarriers.has(carrier.scac)">
              <span class="carrier-coming-soon">Coming soon</span>
            </ng-container>
          </div>

          <ng-container *ngIf="supportedCarriers.has(carrier.scac)">
            <div class="carrier-fields">
              <ng-template #credInput let-field="field" let-scac="scac">
                <div class="mb-2">
                  <label class="field-label">{{ field.label }}</label>
                  <input
                    [type]="field.type === 'password' && !showSecrets[scac] ? 'password' : 'text'"
                    class="form-control form-control-sm"
                    [value]="getCredentialValue(scac, field.key)"
                    (input)="setCredentialValue(scac, field.key, $any($event.target).value)"
                    [placeholder]="field.label"
                    [attr.name]="'cred-' + scac + '-' + field.key"
                    autocomplete="new-password"
                    readonly
                    (focus)="$any($event.target).removeAttribute('readonly')">
                </div>
              </ng-template>

              <ng-container *ngIf="!carrier.groups">
                <ng-container *ngFor="let field of carrier.fields">
                  <ng-container *ngTemplateOutlet="credInput; context: {field: field, scac: carrier.scac}"></ng-container>
                </ng-container>
              </ng-container>

              <ng-container *ngIf="carrier.groups">
                <ng-container *ngFor="let group of carrier.groups">
                  <button
                    type="button"
                    class="field-group-toggle"
                    (click)="toggleGroup(carrier, group.key)">
                    <span class="field-group-toggle__chevron" [class.field-group-toggle__chevron--open]="isGroupOpen(carrier, group.key)">&#9656;</span>
                    {{ group.label }}
                  </button>
                  <ng-container *ngIf="isGroupOpen(carrier, group.key)">
                    <ng-container *ngFor="let field of groupFields(carrier, group.key)">
                      <ng-container *ngTemplateOutlet="credInput; context: {field: field, scac: carrier.scac}"></ng-container>
                    </ng-container>
                  </ng-container>
                </ng-container>
              </ng-container>

            </div>

            <div class="carrier-actions">
              <label class="show-key">
                <input type="checkbox" [(ngModel)]="showSecrets[carrier.scac]"> Show credentials
              </label>
              <span *ngIf="!unsavedCarriers.has(carrier.scac) && healthCheckStatus[carrier.scac]" class="health-badge"
                [class.health-badge--ok]="healthCheckStatus[carrier.scac]?.status === 'OK'"
                [class.health-badge--fail]="healthCheckStatus[carrier.scac]?.status !== 'OK'">
                <span class="health-badge__icon">{{ healthCheckStatus[carrier.scac]?.status === 'OK' ? '\u2713' : '\u2717' }}</span>
                {{ healthLabel(carrier.scac) }}
              </span>
              <div class="carrier-actions__buttons">
                <span *ngIf="unsavedCarriers.has(carrier.scac)" class="carrier-unsaved-hint">Unsaved — use "Save All Credentials" at the bottom of the page, then test</span>
                <button
                  *ngIf="!unsavedCarriers.has(carrier.scac)"
                  class="health-test-btn"
                  [class.health-test-btn--loading]="healthCheckLoading[carrier.scac]"
                  [disabled]="healthCheckLoading[carrier.scac] || !hasCredentials(carrier.scac)"
                  (click)="runHealthCheck(carrier.scac)">
                  <span *ngIf="healthCheckLoading[carrier.scac]" class="spinner-border spinner-border-sm"></span>
                  <span *ngIf="!healthCheckLoading[carrier.scac]">Test connection</span>
                </button>
                <ng-container *ngIf="hasCredentials(carrier.scac)">
                  <button
                    *ngIf="!confirmingDelete[carrier.scac]"
                    class="carrier-clear-btn"
                    [disabled]="clearingCarrier[carrier.scac]"
                    (click)="confirmingDelete[carrier.scac] = true">
                    <span *ngIf="clearingCarrier[carrier.scac]" class="spinner-border spinner-border-sm"></span>
                    <span *ngIf="!clearingCarrier[carrier.scac]">Delete credentials</span>
                  </button>
                  <ng-container *ngIf="confirmingDelete[carrier.scac]">
                    <span class="carrier-clear-confirm">Are you sure?</span>
                    <button
                      class="carrier-clear-btn carrier-clear-btn--confirm"
                      (click)="confirmClearCredentials(carrier.scac)">
                      Yes, delete
                    </button>
                    <button
                      class="carrier-cancel-btn"
                      (click)="confirmingDelete[carrier.scac] = false">
                      Cancel
                    </button>
                  </ng-container>
                </ng-container>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </div>

    <div class="mt-3">
      <button
        class="btn btn-primary px-4"
        [disabled]="savingCredentials"
        (click)="saveCarrierCredentials()">
        <span *ngIf="savingCredentials" class="spinner-border spinner-border-sm me-1"></span>
        Save All Credentials
      </button>
    </div>

    <div class="alert mt-3" [class.alert-success]="credentialsMessageType === 'success'" [class.alert-danger]="credentialsMessageType === 'danger'" *ngIf="credentialsMessage">
      {{ credentialsMessage }}
    </div>
  </div>

  <!-- Polling Tab (dev-only) -->
  <div class="mode-content" *ngIf="activeTab === 'polling' && pollingMetricsEnabled">
    <div class="polling-head">
      <div>
        <h2 class="polling-title">Polling health</h2>
        <p class="mode-hint" style="margin-bottom: 0;">Success rate of carrier polls across your subscriptions.</p>
      </div>
      <div class="win">
        <button [class.active]="pollingWindow === 7" (click)="setPollingWindow(7)">7 days</button>
        <button [class.active]="pollingWindow === 30" (click)="setPollingWindow(30)">30 days</button>
      </div>
    </div>

    <div *ngIf="loadingPolling" class="text-center py-5">
      <div class="spinner-border spinner-border-sm text-muted" role="status"></div>
    </div>

    <div class="alert alert-danger" *ngIf="!loadingPolling && pollingError">
      {{ pollingError }}
    </div>

    <div class="polling-empty" *ngIf="!loadingPolling && !pollingError && pollingRows.length === 0">
      No polling activity yet for this window.
    </div>

    <ng-container *ngIf="!loadingPolling && !pollingError && pollingRows.length > 0">
      <div class="polling-panel">
        <div class="polling-grid polling-grid--head">
          <span>Carrier</span>
          <span>Trend</span>
          <span class="ta-r">Polls</span>
          <span class="ta-r">Errors</span>
          <span class="ta-r">Success</span>
        </div>

        <div
          class="polling-grid polling-row"
          *ngFor="let row of pollingRows; let i = index"
          [style.animation-delay.ms]="i * 55">

          <!-- Carrier -->
          <div class="ident">
            <span class="dot" [ngClass]="row.metric.state"></span>
            <img
              class="clogo"
              [class.clogo--wide]="isWideLogo(row.metric.scacCode)"
              [class.clogo--compact]="isCompactLogo(row.metric.scacCode)"
              [src]="'assets/carriers_logo/' + row.metric.scacCode + '.png'"
              [alt]="row.metric.scacCode">
            <span class="cscac">{{ row.metric.scacCode }}</span>
          </div>

          <!-- Trend (sparkline) -->
          <div class="spark" (mouseleave)="row.activeHit = null">
            <svg viewBox="0 0 300 44" preserveAspectRatio="none">
              <polygon
                [attr.points]="row.sparkArea"
                [attr.fill]="'var(--polling-' + row.metric.state + ')'"
                opacity="0.06"></polygon>
              <!-- non-scaling-stroke keeps the line crisp despite the horizontal stretch -->
              <polyline
                [attr.points]="row.sparkPoints"
                fill="none"
                [attr.stroke]="'var(--polling-' + row.metric.state + ')'"
                stroke-width="1.5"
                stroke-linejoin="round"
                vector-effect="non-scaling-stroke"
                opacity="0.85"></polyline>
              <!-- one invisible full-height band per day → hover target -->
              <rect
                class="hit"
                *ngFor="let hit of row.hits"
                [attr.x]="hit.rectX"
                y="0"
                [attr.width]="hit.rectW"
                height="44"
                (mouseenter)="row.activeHit = hit"></rect>
            </svg>

            <!-- Round HTML overlay dots (perfectly circular regardless of the SVG stretch) -->
            <span
              class="spark-dot"
              [ngClass]="row.metric.state"
              [style.left.%]="row.lastXPct"
              [style.top.%]="row.lastYPct"></span>
            <span
              class="hov-dot"
              *ngIf="row.activeHit"
              [ngClass]="row.metric.state"
              [style.left.%]="row.activeHit.xPct"
              [style.top.%]="row.activeHit.yPct"></span>

            <!-- Per-day tooltip -->
            <div
              class="tip"
              *ngIf="row.activeHit"
              [style.left.%]="row.activeHit.xPct"
              [style.top.%]="row.activeHit.yPct">
              <div class="d">{{ row.activeHit.dayLabel }}</div>
              <div *ngIf="row.activeHit.decided > 0">
                <b>{{ row.activeHit.rate }}%</b><span *ngIf="row.activeHit.ok > 0"> · {{ row.activeHit.ok }}/{{ row.activeHit.decided }} ok</span>
              </div>
              <div class="f" *ngIf="row.activeHit.failed > 0">{{ row.activeHit.failed }} failed</div>
              <div class="nd" *ngIf="row.activeHit.decided === 0">
                {{ row.activeHit.attempts > 0 ? 'no tracking data' : 'no polls' }}
              </div>
            </div>
          </div>

          <!-- Polls -->
          <div class="metric polls">
            <div class="num">{{ row.metric.attempts | number }}</div>
            <div class="cap">{{ pollingWindow }}d</div>
          </div>

          <!-- Errors -->
          <div
            class="metric errors"
            (mouseenter)="row.errorsHover = true"
            (mouseleave)="row.errorsHover = false">
            <div class="num" [class.zero]="row.errorTotal === 0">{{ row.errorTotal }}</div>
            <div class="ebar">
              <div class="fill" *ngIf="row.errorTotal > 0" [style.width.%]="row.errorFillPct">
                <span
                  *ngFor="let seg of row.errorSegments"
                  [style.flex-grow]="seg.grow"
                  [style.flex-basis.px]="0"
                  [style.background]="seg.color"></span>
              </div>
            </div>

            <!-- Per-type breakdown tooltip -->
            <div class="tip etip" *ngIf="row.errorsHover && row.errorTotal > 0">
              <div class="d">{{ row.errorRateLabel }}</div>
              <div class="erow" *ngFor="let b of row.errorBreakdown">
                <span class="sw" [style.background]="b.color"></span>
                <span class="ec">{{ b.count }}</span>
                <span class="el">{{ b.label }}</span>
              </div>
            </div>
          </div>

          <!-- Success -->
          <div class="metric success">
            <div class="num">{{ row.metric.successRate | number:'1.1-1' }}%</div>
            <div class="cap" [ngClass]="row.metric.state">{{ stateLabel(row.metric.state) }}</div>
          </div>
        </div>
      </div>
    </ng-container>
  </div>

  <div *ngIf="loadingConfig && activeTab !== 'polling'" class="text-center py-5">
    <div class="spinner-border spinner-border-sm text-muted" role="status"></div>
  </div>

</section>
